import type { ComputedRef } from 'vue';
import { useTranslation } from 'i18next-vue';
import {
  computed,
  nextTick,
  ref,
  toRef,
} from 'vue';
import { useRouter } from 'vue-router';

import type { CreativesLinkFormSchema } from '@modules/creatives-link/types';
import type {
  FormFlattenKey,
  FormFlattenValue,
  FormGroup,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES } from '@modules/affiliate-program-creatives/constants';
import { TRANSLATION_GROUP_CREATIVES_LINK } from '@modules/creatives-link/constants';
import {
  useConditionTypeOfTransactionListQuery,
  useCreativeAdvertisingCompanyListQuery,
  useCreativeLanguageListQuery,
  useCreativeResolutionListQuery,
  useFormatOfCreativeListQuery,
  useTrackersFilterListQuery,
} from '@modules/creatives-link/queries';
import { AFFILIATE_CREATIVES_LINK_TABLE_ROUTE_NAME } from '@modules/creatives-link/submodules/constants';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useMerchantStore } from '@modules/merchant/store';
import { TRANSLATION_GROUP_SMART_FILTERS } from '@modules/smart-filters/constants';
import { assert, isOptionalNumber, isOptionalString } from '@utils/guards';

interface CreativesLinkPageComposable {
  formSchema: ComputedRef<FormSchema<CreativesLinkFormSchema>>;
  formGroups: ComputedRef<FormGroup<CreativesLinkFormSchema>[]>;
  onChange<
    T extends CreativesLinkFormSchema,
    K extends FormFlattenKey<T>,
  >(key: K, value: FormFlattenValue<T, K> | undefined): Promise<void>;
  onSubmit(data: CreativesLinkFormSchema): void;
}

export default function useCreativesLinkPage(): CreativesLinkPageComposable {
  const merchantList = toRef(useMerchantStore(), 'merchantList');
  const { getTranslation } = useI18n();
  const { t } = useTranslation();
  const router = useRouter();

  const merchant = ref<number>();
  const dealTypeRef = ref<string>();
  const trackerRef = ref<string>();
  const creativeFormatRef = ref<string>();
  const creativeLanguageRef = ref<string>();
  const creativeResolutionRef = ref<string>();
  const creativeCompanyRef = ref<string>();

  const { data: conditionTypeOfTransactionData, isFetching: isTypeOfTransactionLoading } = useConditionTypeOfTransactionListQuery(merchant);
  const { data: trackerFiltersData, isFetching: isTrackersLoading } = useTrackersFilterListQuery(merchant, dealTypeRef);
  const { data: creativeFormatData, isFetching: isCreativeFormatLoading } = useFormatOfCreativeListQuery(merchant, trackerRef);
  const { data: creativeLanguageData, isFetching: isCreativeLanguageLoading } = useCreativeLanguageListQuery(merchant, creativeFormatRef);
  const {
    data: creativeResolutionsData,
    isFetching: isCreativeResolutionLoading,
  } = useCreativeResolutionListQuery(merchant, creativeFormatRef, creativeLanguageRef);
  const {
    data: creativeCompanyData,
    isFetching: isCreativeCompanyLoading,
  } = useCreativeAdvertisingCompanyListQuery(merchant, creativeResolutionRef, creativeFormatRef, creativeLanguageRef);

  const merchantOptions = computed<IFormSelectInputOption<number>[]>(() => (
    merchantList.value.map((item) => ({
      label: item.merchant_name,
      value: item.id,
    }))
  ));
  const dataTypeOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!conditionTypeOfTransactionData.value?.list_condition_type_of_transaction.length) {
      return [];
    }
    return conditionTypeOfTransactionData.value?.list_condition_type_of_transaction.map((item) => ({
      label: getTranslation(TRANSLATION_GROUP_SMART_FILTERS, `TYPE_OF_TRANSACTION.${item.toUpperCase()}`).value,
      value: item,
    }));
  });
  const trackerOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!trackerFiltersData.value?.list.length) {
      return [];
    }
    return trackerFiltersData.value?.list.map((item) => ({
      label: String(item.value),
      value: String(item.id),
    }));
  });
  const creativeFormatOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!creativeFormatData.value?.list_format_of_creative.length) {
      return [];
    }
    return creativeFormatData.value?.list_format_of_creative.map((item) => ({
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, `TAB_${item.toUpperCase()}`).value,
      value: item,
    }));
  });
  const creativeLanguageOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!creativeLanguageData.value?.list.length) {
      return [];
    }
    return creativeLanguageData.value?.list.map((item) => ({
      label: t(`creative.list_locales.${item}`),
      value: item,
    }));
  });
  const creativeSizeOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!creativeResolutionsData.value?.list.length) {
      return [];
    }
    return creativeResolutionsData.value?.list.map((item) => ({
      label: item.value === 'all'
        ? getTranslation(TRANSLATION_GROUP_CREATIVES_LINK, 'ALL_SIZES').value
        : String(item.value),
      value: String(item.creative_resolution_id),
    }));
  });
  const creativeCompanyOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!creativeCompanyData.value?.list.length) {
      return [];
    }
    return creativeCompanyData.value?.list.map((item) => ({
      label: item.value === 'all'
        ? getTranslation(TRANSLATION_GROUP_CREATIVES_LINK, 'ALL_CAMPAIGNS').value
        : String(item.value),
      value: String(item.creative_advertising_company_id),
    }));
  });

  const formSchema = computed<FormSchema<CreativesLinkFormSchema>>(() => {
    const schema: FormSchema<CreativesLinkFormSchema> = {
      merchant: {
        type: EFormInputTypes.SELECT,
        options: merchantOptions.value,
        label: getTranslation(TRANSLATION_GROUP_CREATIVES_LINK, 'MERCHANT').value,
        value: merchant.value,
        required: true,
      },
      deal_type: {
        type: EFormInputTypes.SELECT,
        options: dataTypeOptions.value,
        label: getTranslation(TRANSLATION_GROUP_CREATIVES_LINK, 'CONDITION_TYPE').value,
        value: dealTypeRef.value,
        disabled: !merchant.value,
        loading: isTypeOfTransactionLoading.value,
        required: true,
      },
      serial_id: {
        type: EFormInputTypes.SELECT,
        options: trackerOptions.value,
        label: getTranslation(TRANSLATION_GROUP_CREATIVES_LINK, 'TRACKER').value,
        value: trackerRef.value,
        disabled: !dealTypeRef.value,
        loading: isTrackersLoading.value,
        required: true,
      },
      format: {
        type: EFormInputTypes.SELECT,
        options: creativeFormatOptions.value,
        label: getTranslation(TRANSLATION_GROUP_CREATIVES_LINK, 'CREATIVE_TYPE').value,
        value: creativeFormatRef.value,
        disabled: !trackerRef.value,
        loading: isCreativeFormatLoading.value,
        required: true,
      },
      language: {
        type: EFormInputTypes.SELECT,
        options: creativeLanguageOptions.value,
        label: getTranslation(TRANSLATION_GROUP_CREATIVES_LINK, 'LANGUAGE').value,
        value: creativeLanguageRef.value,
        disabled: !creativeFormatRef.value,
        loading: isCreativeLanguageLoading.value,
        required: true,
      },
      creative_advertising_company_id: {
        type: EFormInputTypes.SELECT,
        options: creativeCompanyOptions.value,
        label: getTranslation(TRANSLATION_GROUP_CREATIVES_LINK, 'CAMPAIGN').value,
        value: creativeCompanyRef.value,
        disabled: creativeFormatRef.value !== 'link' ? !creativeResolutionRef.value : !creativeLanguageRef.value,
        loading: isCreativeCompanyLoading.value,
        required: true,
      },
    };

    if (creativeFormatRef.value !== 'link') {
      schema.creative_resolution_id = {
        type: EFormInputTypes.SELECT,
        options: creativeSizeOptions.value,
        label: getTranslation(TRANSLATION_GROUP_CREATIVES_LINK, 'SIZE').value,
        value: creativeResolutionRef.value,
        disabled: !creativeLanguageRef.value,
        loading: isCreativeResolutionLoading.value,
        required: true,
      };
    }

    return schema;
  });

  const formGroups = computed<FormGroup<CreativesLinkFormSchema>[]>(() => ([
    {
      items: creativeFormatRef.value !== 'link'
        ? [
            'merchant',
            'deal_type',
            'serial_id',
            'format',
            'language',
            'creative_resolution_id',
            'creative_advertising_company_id',
          ]
        : [
            'merchant',
            'deal_type',
            'serial_id',
            'format',
            'language',
            'creative_advertising_company_id',
          ],
    },
  ]));

  async function onChange<
    T extends CreativesLinkFormSchema,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
  ): Promise<void> {
    if (key === 'merchant') {
      assert(isOptionalNumber(value));
      merchant.value = value;

      dealTypeRef.value = undefined;
      trackerRef.value = undefined;
      creativeFormatRef.value = undefined;
      creativeLanguageRef.value = undefined;
      creativeResolutionRef.value = undefined;
      creativeCompanyRef.value = undefined;
    }

    if (key === 'deal_type') {
      assert(isOptionalString(value));
      dealTypeRef.value = value;

      trackerRef.value = undefined;
      creativeFormatRef.value = undefined;
      creativeLanguageRef.value = undefined;
      creativeResolutionRef.value = undefined;
      creativeCompanyRef.value = undefined;
    }

    if (key === 'serial_id') {
      assert(isOptionalString(value));
      trackerRef.value = value;

      creativeFormatRef.value = undefined;
      creativeLanguageRef.value = undefined;
      creativeResolutionRef.value = undefined;
      creativeCompanyRef.value = undefined;
    }

    if (key === 'format') {
      assert(isOptionalString(value));

      creativeLanguageRef.value = undefined;
      creativeResolutionRef.value = undefined;
      creativeCompanyRef.value = undefined;

      /*
      * It's necessary to wait before updating creativeFormatRef because creativeResolutionRef is a conditional field
      * that preserves the previous value(in VForm) after hiding and showing it again
      */
      await nextTick();

      creativeFormatRef.value = value;
    }

    if (key === 'language') {
      assert(isOptionalString(value));
      creativeLanguageRef.value = value;

      creativeResolutionRef.value = undefined;
      creativeCompanyRef.value = undefined;
    }

    if (key === 'creative_resolution_id') {
      assert(isOptionalString(value));
      creativeResolutionRef.value = value;

      creativeCompanyRef.value = undefined;
    }

    if (key === 'creative_advertising_company_id') {
      assert(isOptionalString(value));
      creativeCompanyRef.value = value;
    }
  }

  function onSubmit(data: CreativesLinkFormSchema): void {
    void router.push({
      name: AFFILIATE_CREATIVES_LINK_TABLE_ROUTE_NAME,
      params: {
        merchant: data.merchant,
        dealType: data.deal_type,
        serialId: data.serial_id,
        format: data.format,
        language: data.language,
        creativeAdvertisingCompanyId: data.creative_advertising_company_id,
        creativeResolutionId: data.creative_resolution_id,
      },
    });
  }

  return {
    formSchema,
    formGroups,
    onChange,
    onSubmit,
  };
}
